import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Plus, Trash2, ShieldAlert, Save } from "lucide-react";
import { useAdmin } from "./AdminPages";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

export const AdminMedicalKeywordsPage = () => {
  const { admin, token } = useAdmin();
  const navigate = useNavigate();
  const [keywords, setKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    if (!admin) {
      navigate("/admin/login");
      return;
    }
    void fetchKeywords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin]);

  const fetchKeywords = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/admin/medical-keywords`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setKeywords(res.data?.keywords || []);
    } catch {}
    setLoading(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/admin/medical-keywords`,
        { keywords },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSavedAt(new Date());
      setTimeout(() => setSavedAt(null), 4000);
    } catch (e) {
      alert("Erreur lors de l'enregistrement.");
    }
    setSaving(false);
  };

  const addKeyword = () => {
    const k = newKeyword.trim();
    if (!k) return;
    if (keywords.some((x) => x.toLowerCase() === k.toLowerCase())) {
      alert("Ce mot est déjà dans la liste.");
      return;
    }
    setKeywords([...keywords, k]);
    setNewKeyword("");
  };

  const removeKeyword = (kw) => {
    setKeywords(keywords.filter((x) => x !== kw));
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Link
              to="/admin/dashboard"
              className="p-2 rounded-lg hover:bg-slate-100"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </Link>
            <div>
              <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-amber-600" />
                Filtre médical du chat
              </h1>
              <p className="text-sm text-slate-500">
                Mots-clés bloqués automatiquement dans la messagerie patient ↔ praticien.
              </p>
            </div>
          </div>
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white text-sm font-semibold flex items-center gap-2"
            data-testid="save-keywords-btn"
          >
            <Save className="w-4 h-4" />
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
        {savedAt && (
          <div className="bg-emerald-50 border-t border-emerald-200 text-emerald-700 text-sm px-4 py-2 text-center">
            ✓ Liste enregistrée à {savedAt.toLocaleTimeString("fr-FR")}
          </div>
        )}
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-sm text-amber-800">
          <strong>⚠️ À utiliser avec précaution.</strong> Tout message contenant
          un de ces mots (insensible à la casse et aux accents, formes plurielles
          incluses) sera <strong>refusé</strong> à l'envoi. Évitez les mots trop
          génériques (ex. "mal" → bloquerait "Marseille").
        </div>

        {/* Add new keyword */}
        <div className="flex gap-2 mb-6">
          <input
            type="text"
            value={newKeyword}
            onChange={(e) => setNewKeyword(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addKeyword()}
            placeholder="Ajouter un mot (ex: insuline)"
            className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 outline-none"
            data-testid="new-keyword-input"
          />
          <button
            onClick={addKeyword}
            className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold flex items-center gap-2"
            data-testid="add-keyword-btn"
          >
            <Plus className="w-4 h-4" /> Ajouter
          </button>
        </div>

        {loading ? (
          <p className="text-center text-slate-500 py-12">Chargement…</p>
        ) : (
          <div>
            <p className="text-sm text-slate-600 mb-3">
              <strong>{keywords.length}</strong> mots-clés actuellement bloqués
            </p>
            <div className="bg-white rounded-2xl border border-slate-200 p-4">
              <div className="flex flex-wrap gap-2">
                {keywords.map((kw) => (
                  <span
                    key={kw}
                    className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-800 px-3 py-1.5 rounded-full text-sm"
                    data-testid={`kw-${kw}`}
                  >
                    {kw}
                    <button
                      onClick={() => removeKeyword(kw)}
                      className="hover:text-red-600"
                      aria-label={`Retirer ${kw}`}
                      data-testid={`kw-remove-${kw}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminMedicalKeywordsPage;
